import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Users, GraduationCap, Sparkles, Bike, CircleDollarSign, Coffee, House, Truck, Package } from "lucide-react";
import { IMAGES, SERVICE_LOCATIONS } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

const STATIC = [
  {
    id: "make-it-yours",
    icon: Bike,
    title: "Make It Yours",
    subtitle: "Custom builds & bike revivals",
    desc: "Got an old frame in the garage? A dream bike you've only seen online? A custom cargo e-bike to haul kids and groceries? Let's dream it together and build the bike you've always wanted. We source, spec, and build every component around YOU.",
    imageKey: "noteworthy",
    bullets: [
      "Old frame revivals and restorations",
      "Fully custom cargo e-bike builds",
      "Commuter, gravel, MTB, road — you name it",
      "Transparent parts sourcing",
    ],
    accent: "leaf",
  },
  {
    id: "no-ego",
    icon: Wrench,
    title: "No-Ego Service",
    subtitle: "Learn the craft alongside us",
    desc: "Which tools? Who to ask? Where to learn real tips from real mechanics? Come to the shop, ask anything, work on your own bike with guidance, and discover a new way to get involved with your local bike shop — no judgment, just good wrench time.",
    imageKey: "workshop",
    bullets: [
      "Open wrench time with coaching",
      "Bring your bike, borrow the tools",
      "Honest diagnostics and advice",
      "Youth & teen-welcome hours",
    ],
    accent: "clay",
  },
];

export default function Services() {
  const { settings } = useSettings();
  const IMG = { ...IMAGES, ...(settings.images || {}) };
  const clinics = (settings.services || []).find((s) => s.key === "clinics");

  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="px-4 pt-12 pb-8 sm:px-8 md:px-12 md:pt-16">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
            What we do
          </p>
          <h1 className="mt-3 font-display text-5xl font-black leading-tight tracking-tight text-leaf-900 sm:text-6xl md:text-7xl">
            Services built around <span className="marker-underline italic text-leaf-700">people</span>, not parts.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-leaf-900/75">
            Whether you want a quick fix, a teaching moment, or the custom bike
            of your dreams — there's a place here for you.
          </p>
        </div>
      </section>

      {/* Make it yours + No-ego service */}
      <section className="space-y-20 px-4 py-10 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl space-y-20">
          {STATIC.map((s, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                id={s.id}
                key={s.id}
                data-testid={`service-section-${s.id}`}
                className="grid items-center gap-10 md:gap-14 md:grid-cols-2"
              >
                <div className={reverse ? "md:order-2" : ""}>
                  <div
                    className={`mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${
                      s.accent === "leaf"
                        ? "bg-leaf-100 text-leaf-700"
                        : "bg-clay-500/15 text-clay-600"
                    }`}
                  >
                    <s.icon className="h-7 w-7" />
                  </div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-leaf-700">
                    {s.subtitle}
                  </p>
                  <h2 className="mt-2 font-display text-4xl font-bold leading-tight text-leaf-900 sm:text-5xl">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-leaf-900/80">{s.desc}</p>
                  <ul className="mt-6 grid gap-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-leaf-900">
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-leaf-600" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact#booking"
                    data-testid={`service-cta-${s.id}`}
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-leaf-600 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
                  >
                    Request this service <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className={reverse ? "md:order-1" : ""}>
                  <div className="relative overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_20px_60px_rgba(21,128,61,0.15)]">
                    <img
                      src={IMG[s.imageKey]}
                      alt={s.title}
                      className="h-[360px] w-full object-cover sm:h-[440px]"
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Advanced Clinics from settings */}
          {clinics && (
            <div
              id="advanced-clinics"
              data-testid="service-section-clinics"
              className="grid items-start gap-10 md:gap-14 md:grid-cols-2"
            >
              <div>
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sun-100 text-sun-600">
                  <GraduationCap className="h-7 w-7" />
                </div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-leaf-700">
                  Hands-on, focused learning
                </p>
                <h2 className="mt-2 font-display text-4xl font-bold leading-tight text-leaf-900 sm:text-5xl">
                  {clinics.category}
                </h2>
                <p className="mt-5 text-leaf-900/80">
                  Component-specific, hands-on, focused repair classes will take
                  you through a practical learning session with the goal of
                  taking away the skills required to maintain your bike or fix
                  it trailside. Small groups of 2 to 4 can be accommodated.
                  On-site clinics and private events can be arranged.
                </p>
              </div>
              <div className="space-y-3">
                {(clinics.items || []).map((it) => (
                  <article
                    key={it.name}
                    data-testid={`clinic-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-3xl border-2 border-leaf-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-leaf-300 hover:shadow-lg"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display text-2xl font-bold text-leaf-900">
                          {it.name}
                        </h3>
                        {it.note && (
                          <p className="mt-1.5 text-sm text-leaf-900/75">{it.note}</p>
                        )}
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-full bg-sun-100 px-3 py-1.5 text-base font-black text-leaf-900">
                        <CircleDollarSign className="h-4 w-4" />
                        {it.price}
                      </span>
                    </div>
                  </article>
                ))}
                <Link
                  to="/contact#booking"
                  data-testid="clinics-cta"
                  className="mt-2 inline-flex items-center gap-2 rounded-full bg-leaf-600 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
                >
                  Request a clinic <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Where we wrench */}
      <section className="bg-leaf-50 px-4 py-16 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-600">
            Where we wrench
          </p>
          <h2 className="mt-2 font-display text-4xl font-bold text-leaf-900 sm:text-5xl">
            Service, wherever you ride.
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_LOCATIONS.map((l, i) => {
              const Icon = [Coffee, House, Truck, Package][i];
              return (
                <div
                  key={l.value}
                  data-testid={`service-location-${l.value}`}
                  className="rounded-3xl border-2 border-leaf-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-lg"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-leaf-100 text-leaf-600">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold text-leaf-900">
                    {l.label}
                  </h3>
                  {l.note && (
                    <p className="mt-1.5 inline-block rounded-full bg-sun-100 px-3 py-1 text-xs font-bold text-leaf-800">
                      {l.note}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Youth band */}
      <section className="px-4 py-16 sm:px-8 md:px-12">
        <div
          data-testid="services-youth-band"
          className="mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-sun-100 p-8 sm:p-12"
        >
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-leaf-900 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sun-200">
                <Users className="h-3.5 w-3.5" /> Youth Programs
              </div>
              <h3 className="mt-4 font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
                Growing the next generation of bike people.
              </h3>
              <p className="mt-3 text-leaf-900/80">
                Teen apprenticeship days, summer classes, and free kids' bike
                safety checks. Every kid deserves a bike that works.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="services-youth-cta"
              className="rounded-full bg-leaf-900 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-800"
            >
              Ask about youth programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
